import React from 'react';
import {
	AlertActions,
	AlertInfo,
	AlertModal,
	MainHeader,
	PageWrapperLayout,
	Snackbar,
	StyledGrid,
} from 'presentation/components';
import {useMediaQueries} from 'presentation/pages';
import {Button, Box, Divider, Stack, Typography} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {Loading} from './components/Loading';
import {ContextMenu} from './components/ContextMenu';
import {Card} from './components/Card';
import {CurrentLocation} from './components/CurrentLocation';
import {EmptyState} from './components/EmptyState';
import {FavoriteAddressTutorials} from './components/FavoriteAddressTutorials';
import {AddressDependentAssignmentDrawer} from './components/AddressDependentAssignmentDrawer';
import {useListFavoriteAddresses} from './hooks/useListFavoriteAddresses';
import {
	FAVORITE_ADDRESS_TUTORIAL_STEPS,
	TutorialStepsId,
} from './components/FavoriteAddressTutorials/constants';

const {
	title,
	addNewAddress,
	alertRemoveFavoritePlace,
	assignToDependent,
	editFavoritePlace: {successModal},
} = translations['pt-br'].privateFlow.bookingFlow.location;
const {addressesLabel} = translations['pt-br'].privateFlow.profile.changeData.address;
const {addressInfoAlertDescription, deleteCasaModal} =
	translations['pt-br'].privateFlow.profile.changeData.addresses;

export function FavoriteAddressesList() {
	const {isMobile, isTablet, isLaptop} = useMediaQueries();
	const {isEnabled: isGeolocEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_MULTI_ADDRESS_GEOLOC,
	);
	const {
		goToProfile,
		listItems,
		handleContextMenuOpen,
		handleContextMenuClose,
		handleCardClick,
		handleEdit,
		handleDelete,
		handleAddNewAddress,
		handleCloseRemoveFavoriteAddressAlert,
		handleDeleteConfirmed,
		handleCloseDeleteError,
		handleCloseDeleteSuccess,
		openRemoveFavoriteAddressAlert,
		openDeleteCasaModal,
		handleConfirmDeleteCasa,
		handleCloseDeleteCasaModal,
		openDeleteError,
		openDeleteSuccess,
		anchorContextMenuEl,
		isMenuOpen,
		isLoading,
		isDeletingFavoriteAddress,
		loadingCardClickId,
		showAssignToDependent,
		activeDependents,
		openAssignToDependent,
		openAssignSuccessModal,
		assignSuccessData,
		isPostingAssignment,
		openAssignError,
		handleOpenAssignToDependent,
		handleCloseAssignToDependent,
		handleConfirmAssignToDependent,
		handleCloseAssignSuccessModal,
		handleCloseAssignError,
		openEditSuccessModal,
		editSuccessData,
		handleCloseEditSuccessModal,
		successModalConfig,
		isAdult,
		isHolder,
	} = useListFavoriteAddresses();

	return (
		<PageWrapperLayout title={addressesLabel}>
			<MainHeader title={addressesLabel} handleBack={goToProfile} hideProfileMenu />
			{!isLoading && isGeolocEnabled && isAdult ? (
				<CurrentLocation favoriteAddresses={listItems} />
			) : null}
			{isLoading ? <Loading isMobile={isMobile} /> : null}
			{!isLoading && listItems.length === 0 ? (
				<>
					<EmptyState handleAddNewAddress={handleAddNewAddress} showButton={isAdult} />
					<FavoriteAddressTutorials
						step={FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_EMPTY}
						isBlocked={!isAdult}
					/>
				</>
			) : null}
			{!isLoading && listItems.length > 0 ? (
				<Stack
					alignItems="center"
					pb={isMobile || isTablet ? 10 : 0}
					mt={isLaptop ? 2 : 4}
					mx={isLaptop ? 2 : 4}>
					<Stack width={isLaptop ? '100%' : 618} gap={2}>
						{!isMobile && (
							<Typography variant="subtitle2" color="text.secondary">
								{title}
							</Typography>
						)}

						<Stack id={TutorialStepsId.LIST_ADD_BUTTON} gap={2}>
							<StyledGrid.Root>
								{listItems.map((item, index) => (
									<StyledGrid.Item key={index} sm={12} md={6} lg={6}>
										<Card
											item={item}
											handleContextMenuOpen={handleContextMenuOpen}
											handleCardClick={handleCardClick}
											isLoading={loadingCardClickId === item.id}
										/>
									</StyledGrid.Item>
								))}
							</StyledGrid.Root>
							<Divider />
							{isAdult && (
								<Stack alignItems={'end'}>
									<Button
										variant="outlined"
										size="large"
										fullWidth={isLaptop}
										onClick={handleAddNewAddress}>
										{addNewAddress}
									</Button>
								</Stack>
							)}
						</Stack>
						{isHolder && (
							<AlertInfo.Root severity="info">
								<AlertInfo.Description>
									{addressInfoAlertDescription}
								</AlertInfo.Description>
							</AlertInfo.Root>
						)}
					</Stack>
				</Stack>
			) : null}
			{!isLoading && listItems.length > 0 ? (
				<FavoriteAddressTutorials
					step={FAVORITE_ADDRESS_TUTORIAL_STEPS.LIST_WITH_ADDRESSES}
					isBlocked={false}
				/>
			) : null}
			<ContextMenu
				anchorContextMenuEl={anchorContextMenuEl}
				isMenuOpen={isMenuOpen}
				handleContextMenuClose={handleContextMenuClose}
				handleEdit={handleEdit}
				handleDelete={handleDelete}
				handleAssignToDependent={handleOpenAssignToDependent}
				showAssignToDependent={showAssignToDependent}
			/>

			<AlertModal
				headerTitle={alertRemoveFavoritePlace.headerTitle}
				title={alertRemoveFavoritePlace.title}
				variant="info"
				description={alertRemoveFavoritePlace.description}
				open={openRemoveFavoriteAddressAlert}
				onClose={handleCloseRemoveFavoriteAddressAlert}
				footerContent={
					<AlertActions
						primaryText={alertRemoveFavoritePlace.primary}
						secondaryText={alertRemoveFavoritePlace.secondary}
						primaryAction={handleDeleteConfirmed}
						secondaryAction={handleCloseRemoveFavoriteAddressAlert}
						flexDirection="column-reverse"
						isLoading={isDeletingFavoriteAddress}
						isDisabled={isDeletingFavoriteAddress}
					/>
				}
			/>
			<AlertModal
				headerTitle={deleteCasaModal.headerTitle}
				title={deleteCasaModal.title}
				variant="warning"
				description={deleteCasaModal.description}
				open={openDeleteCasaModal}
				onClose={handleCloseDeleteCasaModal}
				footerContent={
					<AlertActions
						primaryText={deleteCasaModal.primaryAction}
						secondaryText={deleteCasaModal.secondaryAction}
						primaryAction={handleConfirmDeleteCasa}
						secondaryAction={handleCloseDeleteCasaModal}
						flexDirection="column-reverse"
					/>
				}
			/>
			<Snackbar
				onClose={handleCloseDeleteError}
				open={openDeleteError}
				message={alertRemoveFavoritePlace.error}
			/>
			<Snackbar
				onClose={handleCloseDeleteSuccess}
				open={openDeleteSuccess}
				message={alertRemoveFavoritePlace.success}
			/>

			<AddressDependentAssignmentDrawer
				isOpen={openAssignToDependent}
				onClose={handleCloseAssignToDependent}
				dependents={activeDependents}
				onConfirm={handleConfirmAssignToDependent}
				isLoading={isPostingAssignment}
			/>
			<AlertModal
				open={openAssignSuccessModal}
				onClose={handleCloseAssignSuccessModal}
				headerTitle={assignToDependent.successModal.headerTitle}
				title={assignToDependent.successModal.title}
				variant="success"
				buttonLabel={assignToDependent.successModal.closeButton}
				description={
					assignSuccessData ? (
						<>
							{assignToDependent.successModal.descriptionPrefix}{' '}
							<Box component="span" fontWeight={700}>
								{assignSuccessData.addressLabel}
							</Box>{' '}
							{assignToDependent.successModal.descriptionMiddle}{' '}
							<Box component="span" fontWeight={700}>
								{assignSuccessData.dependentNames.join(', ')}
							</Box>
							.
						</>
					) : undefined
				}
			/>
			<Snackbar
				onClose={handleCloseAssignError}
				open={openAssignError}
				message={assignToDependent.errorMessage}
			/>

			<AlertModal
				open={openEditSuccessModal}
				onClose={handleCloseEditSuccessModal}
				headerTitle={successModal.headerTitle}
				title={successModal.title}
				variant="success"
				description={
					editSuccessData ? (
						<>
							{successModal.descriptionPrefix}{' '}
							<Box component="span" fontWeight={700}>
								{editSuccessData.addressLabel}
							</Box>{' '}
							{successModalConfig.descriptionSuffix}
						</>
					) : undefined
				}
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							variant="contained"
							size="large"
							onClick={successModalConfig.primaryAction}
							fullWidth>
							{successModalConfig.primaryButtonLabel}
						</Button>

						{successModalConfig.showSecondaryAction && (
							<Button
								variant="outlined"
								size="large"
								onClick={successModalConfig.secondaryAction}
								fullWidth>
								{successModalConfig.secondaryButtonLabel}
							</Button>
						)}
					</Stack>
				}
			/>
		</PageWrapperLayout>
	);
}
